import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useFetch } from "@/lib/content";
import { ImageField } from "@/components/admin/ImageField";

const FIELDS = [
  { name: "site_name", label: "Site name", type: "text" },
  { name: "logo_url", label: "Logo — header (appears on every page)", type: "image" },
  { name: "footer_logo_url", label: "Logo — footer light version (for dark background)", type: "image" },
  { name: "favicon_url", label: "Favicon (square version of the logo)", type: "image" },
  { name: "tagline", label: "Tagline", type: "text" },
  { name: "contact_email", label: "Contact email (public)", type: "text" },
  { name: "phone", label: "Phone (public)", type: "text" },
  { name: "address", label: "Office address", type: "textarea" },
  { name: "calendly_url", label: "Booking page (Google Calendar appointment link, embed code, or Calendly URL)", type: "text" },
  { name: "lead_alert_email", label: "Lead alert inbox (where new leads are emailed)", type: "text" },
  { name: "seo_title", label: "Default SEO title", type: "text" },
  { name: "site_url", label: "Public site URL (used in sitemap & robots)", type: "text", hint: "e.g. https://renbsolutions.com" },
  { name: "seo_description", label: "Default SEO description", type: "textarea" },
  { name: "og_image", label: "Default OG image URL", type: "text" },
  { name: "linkedin_url", label: "LinkedIn URL", type: "text" },
];

export default function SettingsAdmin() {
  const { data: settings, loading } = useFetch("/admin/settings");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <p className="mt-10 text-xs uppercase tracking-[0.25em] text-slategray">Loading…</p>;

  return (
    <div data-testid="admin-settings">
      <h1 className="font-serif text-4xl font-medium tracking-tight">Site Settings</h1>
      <p className="mt-2 text-sm text-slategray">Contact info, the Calendly link, lead alerts and SEO defaults.</p>
      <form onSubmit={save} className="mt-10 grid max-w-3xl gap-8">
        {FIELDS.map((f) => (
          <label key={f.name} className="flex flex-col gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slategray">{f.label}</span>
            {f.type === "textarea" ? (
              <textarea data-testid={`settings-${f.name}`} rows={3} className="field resize-none" value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
            ) : f.type === "image" ? (
              <ImageField testid={`settings-${f.name}`} value={form[f.name] || ""} onChange={(v) => setForm({ ...form, [f.name]: v })} />
            ) : (
              <input data-testid={`settings-${f.name}`} className="field" value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
            )}
          </label>
        ))}
        <div>
          <button
            type="submit"
            data-testid="settings-save"
            disabled={saving}
            className="bg-progress px-8 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-progress-dark disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
